/**
 * DiagnosisInputField - Input component for primary_diagnosis_code array field
 *
 * Manages a list of diagnosis codes (ICD-10) with add/remove functionality.
 *
 * Exports:
 * - DiagnosisInputField: Main input component
 * - DiagnosisInputFieldPreview: Preview component for table cells
 */

import React from 'react';

import type { CodeInputFieldConfig, CodeItem } from './CodeInputField';
import { CodeInputField } from './CodeInputField';
import { SchemaProvider, useSchemaContextSafe } from './context/SchemaContext';
import { PillPreviewList, type PreviewPill } from './PillPreviewList';
import type { InputArrayProps, PreviewProps } from './types';

// Schema field id used by the registry for this component
// (see fields/index.ts inputComponents map).
const SCHEMA_FIELD_ID = 'primary_diagnosis_code';
const CODE_SUBFIELD_ID = 'code';

// =============================================================================
// MAIN INPUT COMPONENT
// =============================================================================

const DIAGNOSIS_CONFIG: CodeInputFieldConfig = {
  label: 'ICD-10 Code',
  placeholder: 'e.g., M06.9',
  emptyMessage: 'No diagnosis codes added',
  addButtonLabel: 'Add Diagnosis Code',
  deleteTitle: 'Delete Diagnosis?',
  deleteDescription:
    'This action cannot be undone. This will permanently delete this diagnosis code entry.',
  itemLabelPrefix: 'Diagnosis',
};

/**
 * Public component — mounts SchemaProvider so the inner body can consume
 * `isFieldRequired` from context. The inner body MUST be a separate component;
 * calling useSchemaContextSafe() in the same component that renders
 * <SchemaProvider> would see the outer (missing) context, not the one it
 * just installed.
 */
export const DiagnosisInputField: React.FC<InputArrayProps> = props => (
  <SchemaProvider>
    <DiagnosisInputFieldInner {...props} />
  </SchemaProvider>
);

const DiagnosisInputFieldInner: React.FC<InputArrayProps> = ({
  data,
  onChange,
  errors = {},
  disabled = false,
}) => {
  // Drive the `*` indicator from the schema. CodeInputField stays
  // schema-agnostic; we pass the resolved boolean in via config.
  const { isFieldRequired } = useSchemaContextSafe();
  const config: CodeInputFieldConfig = {
    ...DIAGNOSIS_CONFIG,
    required: isFieldRequired(CODE_SUBFIELD_ID, SCHEMA_FIELD_ID),
  };

  // CodeInputField expects CodeItem[] - cast from unknown[]
  return (
    <CodeInputField
      data={data as CodeItem[]}
      onChange={onChange as (data: CodeItem[]) => void}
      errors={errors}
      disabled={disabled}
      config={config}
    />
  );
};

// =============================================================================
// PREVIEW COMPONENT
// =============================================================================

/** Item shape for preview (defined locally, schema provides field definitions at runtime) */
interface DiagnosisItem {
  code?: string;
}

/**
 * DiagnosisInputFieldPreview - Pill-based preview component for primary_diagnosis_code array field
 *
 * Displays diagnoses as pills with format: code (monospace)
 */
export const DiagnosisInputFieldPreview: React.FC<PreviewProps> = ({ data, maxItems = 2 }) => {
  const items = (Array.isArray(data) ? data : []) as DiagnosisItem[];

  // `key` need not be unique — PillPreviewList appends the index.
  const pills: PreviewPill[] = items.flatMap(item =>
    item.code ? [{ key: item.code, text: item.code, className: 'font-mono' }] : [],
  );

  return <PillPreviewList pills={pills} maxItems={maxItems} />;
};
